import { SidebarGroup, SidebarItem, useAppShellOptional } from '@vnetwork/ui/layout';
import type { NavItem } from '@vnetwork/ui/shells';

function isActive(item: NavItem, activeId: string): boolean {
  return item.id === activeId || !!item.items?.some((child) => isActive(child, activeId));
}

const labelText = (item: NavItem) => (typeof item.label === 'string' ? item.label : undefined);

/**
 * Sidebar nav rendered by the workspace instead of DashboardLayout's built-in
 * renderer, which drops child icons and, when the sidebar is collapsed, leaves an
 * open group's children as blank icon-less buttons. Here nested items keep their
 * icon, and a collapsed sidebar shows each group as ONE icon (active when any child
 * is) that expands the sidebar on click so the user can pick the child.
 */
export function WorkspaceSidebarNav({
  items,
  activeId,
  onNavigate,
}: {
  items: NavItem[];
  activeId: string;
  onNavigate: (item: NavItem) => void;
}) {
  const shell = useAppShellOptional();
  const collapsed = shell?.collapsed ?? false;

  return (
    <>
      {items.map((item) => {
        const active = isActive(item, activeId);
        if (!item.items?.length) {
          return (
            <SidebarItem key={item.id} icon={item.icon} active={active} badge={item.badge} title={collapsed ? labelText(item) : undefined} onClick={() => onNavigate(item)}>
              {item.label}
            </SidebarItem>
          );
        }
        if (collapsed) {
          return (
            <SidebarItem key={item.id} icon={item.icon} active={active} title={labelText(item)} onClick={() => shell?.setCollapsed(false)}>
              {item.label}
            </SidebarItem>
          );
        }
        return (
          <SidebarGroup key={item.id} icon={item.icon} label={item.label} defaultOpen={item.defaultOpen ?? active}>
            {item.items.map((child) => (
              <SidebarItem
                key={child.id}
                nested
                icon={child.icon}
                active={isActive(child, activeId)}
                badge={child.badge}
                onClick={() => onNavigate(child)}
              >
                {child.label}
              </SidebarItem>
            ))}
          </SidebarGroup>
        );
      })}
    </>
  );
}
